import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagIcon, ZagButton } from '@zag/ui';

export interface WebhookSecretDialogData {
  secret: string;
  endpointName: string;
}

export interface WebhookSecretDialogResult {
  confirmed: boolean;
}

/**
 * One-time secret reveal dialog.
 * Shown immediately after webhook endpoint creation -- the secret is only
 * returned in the create response and is masked `wh_****` thereafter.
 *
 * The dialog CANNOT be closed via backdrop click or Escape key -- the user
 * must explicitly confirm they have saved the secret.
 */
@Component({
  selector: 'zag-webhook-secret-dialog',
  imports: [TranslatePipe, ZagIcon, ZagButton],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="webhook-secret-dialog">
      <div class="webhook-secret-dialog__header">
        <span zagIcon="key" class="webhook-secret-dialog__icon"></span>
        <h2 class="webhook-secret-dialog__title">{{ 'webhooks.secret.title' | translate }}</h2>
      </div>

      <div class="webhook-secret-dialog__warning" role="alert">
        <span zagIcon="warning" class="webhook-secret-dialog__warning-icon"></span>
        <p>{{ 'webhooks.secret.warning' | translate }}</p>
      </div>

      <div class="webhook-secret-dialog__body">
        <p class="webhook-secret-dialog__label">{{ 'webhooks.secret.label' | translate }}</p>
        <div class="webhook-secret-dialog__secret-row">
          <code class="webhook-secret-dialog__secret" [attr.aria-label]="'webhooks.secret.valueLabel' | translate">
            {{ data.secret }}
          </code>
          <button
            type="button"
            class="webhook-secret-dialog__copy-btn"
            zagButton="outlined"
            zagSize="sm"
            [attr.aria-label]="'common.copy' | translate"
            [title]="'common.copy' | translate"
            (click)="copySecret()"
          >
            <span [zagIcon]="copied() ? 'check' : 'content_copy'"></span>
            {{ (copied() ? 'common.copied' : 'common.copy') | translate }}
          </button>
        </div>
      </div>

      <div class="webhook-secret-dialog__footer">
        <button type="button" zagButton (click)="confirm()">
          {{ 'webhooks.secret.confirm' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .webhook-secret-dialog {
        background: var(--background-color-base);
        border-radius: var(--border-radius-lg);
        padding: var(--spacing-xl);
        max-width: 520px;
        width: 100%;
      }

      .webhook-secret-dialog__header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
      }

      .webhook-secret-dialog__icon {
        font-size: 28px;
        color: var(--color-primary);
      }

      .webhook-secret-dialog__title {
        font-size: var(--font-size-h3);
        font-weight: 700;
        color: var(--text-color-primary);
        margin: 0;
      }

      .webhook-secret-dialog__warning {
        display: flex;
        gap: var(--spacing-sm);
        padding: var(--spacing-md);
        background: color-mix(in srgb, var(--color-warning) 12%, transparent);
        border: 1px solid var(--color-warning);
        border-radius: var(--border-radius-md);
        margin-bottom: var(--spacing-md);
        color: var(--text-color-primary);
      }

      .webhook-secret-dialog__warning-icon {
        font-size: 20px;
        color: var(--color-warning);
        flex-shrink: 0;
        margin-top: 2px;
      }

      .webhook-secret-dialog__warning p {
        margin: 0;
        font-size: var(--font-size-body);
      }

      .webhook-secret-dialog__body {
        margin-bottom: var(--spacing-xl);
      }

      .webhook-secret-dialog__label {
        font-size: var(--font-size-caption);
        font-weight: 600;
        color: var(--text-color-secondary);
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin: 0 0 var(--spacing-xs);
      }

      .webhook-secret-dialog__secret-row {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        padding: var(--spacing-sm);
        background: var(--background-color-elevated);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);
      }

      .webhook-secret-dialog__secret {
        flex: 1;
        font-family: monospace;
        font-size: 13px;
        word-break: break-all;
        color: var(--text-color-primary);
        background: none;
        border: none;
        padding: 0;
        user-select: all;
      }

      .webhook-secret-dialog__copy-btn {
        flex-shrink: 0;
      }

      .webhook-secret-dialog__footer {
        display: flex;
        justify-content: flex-end;
      }
    `,
  ],
})
export class WebhookSecretDialogComponent {
  protected readonly data = inject<WebhookSecretDialogData>(DIALOG_DATA);
  private readonly dialogRef = inject(DialogRef<WebhookSecretDialogResult>);

  protected readonly copied = signal(false);

  protected copySecret(): void {
    navigator.clipboard.writeText(this.data.secret).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    });
  }

  protected confirm(): void {
    this.dialogRef.close({ confirmed: true });
  }
}
